import React, { useState, useEffect, useRef } from 'react';
import { getAllRubriques } from '../services/rubrique.service';
import { getAllAffiliation } from '../services/affiliation.service';
import { submitArticle } from '../services/article.service';
import { Link } from "react-router-dom";
import routes from "../routes/routes";
import { useScrollToTop } from '../hooks';

const emptyCoAuthor = () => ({
    first_name: '',
    orch_id: '',
    last_name: '',
    email_user: '',
    affiliations: [],
});
const emptyDocument = (type = '') => ({
    type_doc: type,
    title_doc: '',
    legend_doc: '',
    number_doc: '',
    auth_doc: '',
    source_doc: [{ key: "", value: "" }],
    path_doc: null
});

const allowedFileArtTypes = ['doc', 'docx'];
const allowedImageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
const allowedPdfTypes = ['pdf'];

const FIELD_LABELS = {
    fr: {
        title: "Titre de l'article",
        resume: "Résumé",
        keywords: "Mots-clés",
        title_placeholder: "Titre en français",
        resume_placeholder: "Résumé en français",
        keywords_placeholder: "Ajouter un mot-clé...",
        summary_word_limit: "Limite: 300 mots",
        add_rubrique: "+ Ajouter une rubrique...",
        abstract_word_count: (count) => `${count} / 300 mots`,
        file_main_label: "Fichier principal",
        file_main_format: "(format doc, docx)",
        file_main_placeholder: "Cliquez pour sélectionner un fichier Word (.doc ou .docx)",
        file_main_error: "Seuls les fichiers Word (.doc, .docx) sont acceptés.",
        file_main_valid: "Fichier Word valide",
        language_selector_label: "Langue de l'article :",
        language_choice_fr: "Français",
        language_choice_en: "Anglais",
        language_choice_mg: "Malagasy",
        step2_title: "Étape 2: Informations sur l'article",
        study_area_label: "Zone d'étude",
        study_area_optional: "(optionnel)",
        study_area_placeholder: "ex: Fianarantsoa, Madagascar",
        rubrique_label: "Rubriques",
        rubrique_required: "*",
        rubrique_error: "Sélectionnez au moins une rubrique.",
    },
    en: {
        title: "Title",
        resume: "Abstract",
        keywords: "Keywords",
        title_placeholder: "Title in English",
        resume_placeholder: "Abstract in English",
        keywords_placeholder: "Add a keyword...",
        summary_word_limit: "Limit: 300 words",
        add_rubrique: "+ Add a section...",
        abstract_word_count: (count) => `${count} / 300 words`,
        file_main_label: "Main file",
        file_main_format: "(doc, docx format)",
        file_main_placeholder: "Click to select a Word file (.doc or .docx)",
        file_main_error: "Only Word files (.doc, .docx) are allowed.",
        file_main_valid: "Valid Word file",
        language_selector_label: "Article language:",
        language_choice_fr: "French",
        language_choice_en: "English",
        language_choice_mg: "Malagasy",
        step2_title: "Step 2: Article information",
        study_area_label: "Study area",
        study_area_optional: "(optional)",
        study_area_placeholder: "e.g. Fianarantsoa, Madagascar",
        rubrique_label: "Section",
        rubrique_required: "*",
        rubrique_error: "Select at least one section.",
    },
    mg: {
        title: "Lohateny",
        resume: "Famintinana",
        keywords: "Teny fototra",
        title_placeholder: "Lohateny amin'ny teny Malagasy",
        resume_placeholder: "Famintinana amin'ny teny Malagasy",
        keywords_placeholder: "Ampio teny fototra...",
        summary_word_limit: "Fetra: teny 300",
        add_rubrique: "+ Ampio sokajy...",
        abstract_word_count: (count) => `${count} / teny 300`,
        file_main_label: "Rakitra fototra",
        file_main_format: "(endrika doc, docx)",
        file_main_placeholder: "Tsindrio hanisy rakitra Word (.doc na .docx)",
        file_main_error: "Rakitra Word (.doc, .docx) ihany no azo ekena.",
        file_main_valid: "Rakitra Word mety",
        language_selector_label: "Fitenin'ny lahatsoratra:",
        language_choice_fr: "Frantsay",
        language_choice_en: "Anglisy",
        language_choice_mg: "Malagasy",
        step2_title: "Dingana 2: Momba ny lahatsoratra",
        study_area_label: "Faritra ianarana",
        study_area_optional: "(tsy voatery)",
        study_area_placeholder: "oh: Fianarantsoa, Madagasikara",
        rubrique_label: "Sokajy",
        rubrique_required: "*",
        rubrique_error: "Safidio farafahakeliny sokajy iray.",
    }
};

const LANGS = [
    { code: 'fr', label: FIELD_LABELS.fr.language_choice_fr },
    { code: 'en', label: FIELD_LABELS.en.language_choice_en },
    { code: 'mg', label: FIELD_LABELS.mg.language_choice_mg }
];

const PropositionArticle = () => {
    useScrollToTop();
    const [currentStep, setCurrentStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('fr');
    const [formData, setFormData] = useState({
        author: emptyCoAuthor(),
        co_authors: [emptyCoAuthor()],
        title_art: { fr: '', en: '', mg: '' },
        resume_art: { fr: '', en: '', mg: '' },
        key_words_art: { fr: [], en: [], mg: [] },
        study_area_art: '',
        id_rub: [],
        file_art: null,
        documents: []
    });
    const [rubriques, setRubriques] = useState([]);
    const [affiliations, setAffiliations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAffiliationSuggestions, setShowAffiliationSuggestions] = useState(false);
    const [currentAffiliationInput, setCurrentAffiliationInput] = useState('');
    const [filteredAffiliations, setFilteredAffiliations] = useState([]);
    const [activeAuthorIndex, setActiveAuthorIndex] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [currentKeyword, setCurrentKeyword] = useState('');
    const [fileMainValid, setFileMainValid] = useState(null);
    const keywordInputRef = useRef(null);
    const affiliationInputRefs = useRef({});

    const t = (k) => FIELD_LABELS[selectedLanguage][k] || FIELD_LABELS['fr'][k];

    useEffect(() => {
        const fetchRubriques = async () => {
            setLoading(true);
            try {
                const response = await getAllRubriques();
                if (response.success && Array.isArray(response.data)) {
                    setRubriques(response.data);
                } else {
                    throw new Error('Format de données inattendu');
                }
                setLoading(false);
            } catch (err) {
                setError('Erreur lors du chargement des rubriques');
                setLoading(false);
            }
        };
        const fetchAffiliations = async () => {
            try {
                const response = await getAllAffiliation();
                if (response.success && Array.isArray(response.data)) {
                    setAffiliations(response.data.map(aff => aff.name_aff || ''));
                }
            } catch (err) { }
        };
        fetchRubriques();
        fetchAffiliations();
    }, []);

    useEffect(() => {
        if (currentAffiliationInput) {
            const filtered = affiliations.filter(
                aff => aff.toLowerCase().includes(currentAffiliationInput.toLowerCase())
            );
            setFilteredAffiliations(filtered);
        } else {
            setFilteredAffiliations([]);
        }
    }, [currentAffiliationInput, affiliations]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            let clickedInsideAny = false;
            Object.values(affiliationInputRefs.current).forEach(ref => {
                if (ref && ref.contains(event.target)) {
                    clickedInsideAny = true;
                }
            });
            if (!clickedInsideAny) setShowAffiliationSuggestions(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, []);

    const handleAuthorChange = (e) => {
        const { name, value } = e.target;
        setFormData(f => ({
            ...f,
            author: { ...f.author, [name]: value }
        }));
    };

    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleAffiliationInputChange = (e, index = -1) => {
        const value = e.target.value;
        setCurrentAffiliationInput(value);
        setActiveAuthorIndex(index);
        setShowAffiliationSuggestions(true);
    };

    const addAffiliation = (value, index = -1) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        if (index === -1) {
            setFormData(f => {
                if (f.author.affiliations.includes(trimmed)) return f;
                return {
                    ...f,
                    author: {
                        ...f.author,
                        affiliations: [...f.author.affiliations, trimmed]
                    }
                };
            });
            setCurrentAffiliationInput('');
        } else {
            setFormData(f => {
                const updated = [...f.co_authors];
                if (!updated[index].affiliations.includes(trimmed)) {
                    updated[index] = {
                        ...updated[index],
                        affiliations: [...updated[index].affiliations, trimmed]
                    };
                }
                return { ...f, co_authors: updated };
            });
            setCurrentAffiliationInput('');
        }
    };

    const removeAffiliation = (index, aff) => {
        if (index === -1) {
            setFormData(f => ({
                ...f,
                author: {
                    ...f.author,
                    affiliations: f.author.affiliations.filter(a => a !== aff)
                }
            }));
        } else {
            setFormData(f => {
                const updated = [...f.co_authors];
                updated[index].affiliations = updated[index].affiliations.filter(a => a !== aff);
                return { ...f, co_authors: updated };
            });
        }
    };

    const handleAffiliationInputKeyDown = (e, index = -1) => {
        // N'ajoute une affiliation que sur Entrée, virgule ou point-virgule, PAS sur espace !
        if (
            (e.key === 'Enter' || e.key === ',' || e.key === ';') &&
            currentAffiliationInput.trim() !== ''
        ) {
            e.preventDefault();
            addAffiliation(currentAffiliationInput, index);
        } else if (
            e.key === 'Backspace' &&
            currentAffiliationInput === '' &&
            ((index === -1 && formData.author.affiliations.length > 0) ||
                (index >= 0 && formData.co_authors[index].affiliations.length > 0))
        ) {
            if (index === -1) {
                setFormData(f => ({
                    ...f,
                    author: {
                        ...f.author,
                        affiliations: f.author.affiliations.slice(0, -1)
                    }
                }));
            } else {
                setFormData(f => {
                    const updated = [...f.co_authors];
                    updated[index].affiliations = updated[index].affiliations.slice(0, -1);
                    return { ...f, co_authors: updated };
                });
            }
        }
    };

    const handleAffiliationSelect = (affiliation) => {
        addAffiliation(affiliation, activeAuthorIndex);
        setShowAffiliationSuggestions(false);
    };

    const handleArticleChange = (e) => {
        const { name, value, options } = e.target;
        if (name === "id_rub") {
            const selected = Array.from(options)
                .filter(opt => opt.selected)
                .map(opt => Number(opt.value));
            setFormData(f => ({
                ...f,
                id_rub: selected
            }));
        } else if (name === "title_art" || name === "resume_art") {
            setFormData(f => ({
                ...f,
                [name]: {
                    ...f[name],
                    [selectedLanguage]: value
                }
            }));
        } else {
            setFormData(f => ({
                ...f,
                [name]: value
            }));
        }
    };

    const handleLanguageChange = (e) => setSelectedLanguage(e.target.value);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const ext = file.name.split('.').pop().toLowerCase();
            if (allowedFileArtTypes.includes(ext)) {
                setFileMainValid(true);
                setFormData(f => ({
                    ...f,
                    file_art: file
                }));
            } else {
                setFileMainValid(false);
                setFormData(f => ({
                    ...f,
                    file_art: file
                }));
            }
        } else {
            setFileMainValid(null);
            setFormData(f => ({
                ...f,
                file_art: null
            }));
        }
    };

    const handleKeywordInput = (e) => setCurrentKeyword(e.target.value);

    const addKeyword = (value) => {
        const trimmed = value.trim();
        if (
            trimmed &&
            !formData.key_words_art[selectedLanguage].includes(trimmed)
        ) {
            setFormData(f => ({
                ...f,
                key_words_art: {
                    ...f.key_words_art,
                    [selectedLanguage]: [...f.key_words_art[selectedLanguage], trimmed]
                }
            }));
            setCurrentKeyword('');
        }
    };

    const handleKeywordKeyDown = (e) => {
        if (
            (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') &&
            currentKeyword.trim() !== ''
        ) {
            e.preventDefault();
            addKeyword(currentKeyword);
        } else if (e.key === 'Backspace' && currentKeyword === '' && formData.key_words_art[selectedLanguage].length > 0) {
            setFormData(f => ({
                ...f,
                key_words_art: {
                    ...f.key_words_art,
                    [selectedLanguage]: f.key_words_art[selectedLanguage].slice(0, -1)
                }
            }));
        }
    };

    const removeKeyword = (k) => {
        setFormData(f => ({
            ...f,
            key_words_art: {
                ...f.key_words_art,
                [selectedLanguage]: f.key_words_art[selectedLanguage].filter(word => word !== k)
            }
        }));
    };

    const handleCoAuthorChange = (index, e) => {
        const { name, value } = e.target;
        setFormData(f => {
            const updated = [...f.co_authors];
            updated[index] = { ...updated[index], [name]: value };
            return { ...f, co_authors: updated };
        });
    };

    const addCoAuthor = () => {
        setFormData(f => ({
            ...f,
            co_authors: [...f.co_authors, emptyCoAuthor()]
        }));
    };

    const removeCoAuthor = (index) => {
        setFormData(f => {
            const updated = [...f.co_authors];
            updated.splice(index, 1);
            return { ...f, co_authors: updated.length ? updated : [emptyCoAuthor()] };
        });
    };

    const handleDocumentTypeAdd = (type) => {
        setFormData(f => ({
            ...f,
            documents: [...f.documents, emptyDocument(type)]
        }));
    };

    const handleDocumentChange = (index, e) => {
        const { name, value, files } = e.target;
        setFormData(f => {
            const updated = [...f.documents];
            if (name === "path_doc" && files && files.length) {
                updated[index][name] = files[0];
            } else {
                updated[index][name] = value;
            }
            return { ...f, documents: updated };
        });
    };

    const handleDocumentSourceChange = (index, sourceIdx, keyOrValue, value) => {
        setFormData(f => {
            const updated = [...f.documents];
            const sources = [...updated[index].source_doc];
            sources[sourceIdx][keyOrValue] = value;
            updated[index].source_doc = sources;
            return { ...f, documents: updated };
        });
    };

    const addDocumentSource = (docIdx) => {
        setFormData(f => {
            const updated = [...f.documents];
            updated[docIdx].source_doc.push({ key: "", value: "" });
            return { ...f, documents: updated };
        });
    };

    const removeDocumentSource = (docIdx, srcIdx) => {
        setFormData(f => {
            const updated = [...f.documents];
            updated[docIdx].source_doc.splice(srcIdx, 1);
            if (updated[docIdx].source_doc.length === 0) {
                updated[docIdx].source_doc.push({ key: "", value: "" });
            }
            return { ...f, documents: updated };
        });
    };

    const removeDocument = (index) => {
        setFormData(f => {
            const updated = [...f.documents];
            updated.splice(index, 1);
            return { ...f, documents: updated };
        });
    };

    const validateStep = (step) => {
        const errors = {};
        if (step === 1) {
            const { author, co_authors } = formData;
            if (!author.first_name.trim()) errors.author_first_name = "Le prénom est obligatoire.";
            if (!author.last_name.trim()) errors.author_last_name = "Le nom est obligatoire.";
            if (!author.email_user.trim()) errors.author_email_user = "L'email est obligatoire.";
            else if (!isValidEmail(author.email_user.trim())) errors.author_email_user = "Format d'email invalide.";
            if (!author.affiliations.length) errors.author_affiliations = "Au moins une affiliation est requise.";
            co_authors.forEach((co, idx) => {
                const filled = Object.values(co).some(val => Array.isArray(val) ? val.length > 0 : val.trim());
                if (filled) {
                    if (!co.first_name.trim()) errors[`coauthor_${idx}_first_name`] = "Prénom requis";
                    if (!co.last_name.trim()) errors[`coauthor_${idx}_last_name`] = "Nom requis";
                    if (!co.email_user.trim()) errors[`coauthor_${idx}_email_user`] = "Email requis";
                    else if (!isValidEmail(co.email_user.trim())) errors[`coauthor_${idx}_email_user`] = "Format d'email invalide";
                    if (!co.affiliations.length) errors[`coauthor_${idx}_affiliations`] = "Au moins une affiliation est requise";
                }
            });
        }
        if (step === 2) {
            if (!formData.title_art[selectedLanguage].trim())
                errors.title_art = `Le titre est obligatoire (${LANGS.find(l => l.code === selectedLanguage)?.label || selectedLanguage}).`;
            if (!formData.resume_art[selectedLanguage].trim())
                errors.resume_art = `Le résumé est obligatoire (${LANGS.find(l => l.code === selectedLanguage)?.label || selectedLanguage}).`;
            const summaryWords = formData.resume_art[selectedLanguage]?.trim().split(/\s+/).length || 0;
            if (summaryWords > 300) errors.resume_art = "Le résumé doit comporter 300 mots maximum.";
            if (!formData.key_words_art[selectedLanguage].length)
                errors.key_words_art = `Les mots-clés sont obligatoires (${LANGS.find(l => l.code === selectedLanguage)?.label || selectedLanguage}).`;
            if (!formData.id_rub.length) errors.id_rub = t("rubrique_error");
            if (!formData.file_art) errors.file_art = t("file_main_error");
            else {
                const ext = formData.file_art.name.split('.').pop().toLowerCase();
                if (!allowedFileArtTypes.includes(ext)) {
                    errors.file_art = t("file_main_error");
                    setFileMainValid(false);
                } else {
                    setFileMainValid(true);
                }
            }
        }
        if (step === 3) {
            formData.documents.forEach((doc, i) => {
                if (doc.type_doc === 'image') {
                    if (!doc.path_doc)
                        errors[`doc_${i}_path_doc`] = "Le fichier image est obligatoire";
                    else if (typeof doc.path_doc === "object") {
                        const ext = doc.path_doc.name.split('.').pop().toLowerCase();
                        if (!allowedImageTypes.includes(ext))
                            errors[`doc_${i}_path_doc`] = "Fichier image uniquement (jpg, jpeg, png, gif, bmp, webp)";
                    }
                } else if (doc.type_doc === 'pdf') {
                    if (doc.path_doc && typeof doc.path_doc === "object") {
                        const ext = doc.path_doc.name.split('.').pop().toLowerCase();
                        if (!allowedPdfTypes.includes(ext))
                            errors[`doc_${i}_path_doc`] = "Fichier PDF uniquement";
                    }
                }
            });
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) setCurrentStep(c => c + 1);
    };
    const prevStep = () => setCurrentStep(c => c - 1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(3)) return;
        setSubmitting(true);
        setSubmissionError(null);
        try {
            if (!formData.file_art) throw new Error("Le document principal (file_art) est obligatoire.");
            const ext = formData.file_art.name.split('.').pop().toLowerCase();
            if (!allowedFileArtTypes.includes(ext)) throw new Error(t("file_main_error"));
            for (const doc of formData.documents) {
                if (doc.path_doc && typeof doc.path_doc !== "string") {
                    const docExt = doc.path_doc.name.split('.').pop().toLowerCase();
                    if (doc.type_doc === 'image' && !allowedImageTypes.includes(docExt)) throw new Error("Les figures doivent être image");
                    if (doc.type_doc === 'pdf' && docExt !== 'pdf') throw new Error("Les annexes PDF doivent être PDF");
                }
            }
            const fd = new FormData();
            fd.append('file_art', formData.file_art);
            fd.append('study_area_art', formData.study_area_art);
            fd.append('author', JSON.stringify({ ...formData.author, affiliations: formData.author.affiliations.join(', ') }));
            const title_art = { [selectedLanguage]: formData.title_art[selectedLanguage] };
            const resume_art = { [selectedLanguage]: formData.resume_art[selectedLanguage] };
            const key_words_art = { [selectedLanguage]: formData.key_words_art[selectedLanguage] };
            fd.append('key_words_art', JSON.stringify(key_words_art));
            fd.append('resume_art', JSON.stringify(resume_art));
            fd.append('title_art', JSON.stringify(title_art));
            fd.append('language', selectedLanguage);
            if (formData.id_rub && Array.isArray(formData.id_rub) && formData.id_rub.length) {
                fd.append('id_rub', formData.id_rub.join(','));
            } else {
                throw new Error(t("rubrique_error"));
            }
            const validCoAuthors = formData.co_authors.filter(
                a => a.first_name.trim() && a.last_name.trim()
            ).map(a => ({
                ...a,
                affiliations: a.affiliations.join(', ')
            }));
            if (validCoAuthors.length) {
                fd.append('co_authors', JSON.stringify(validCoAuthors));
            }
            for (let i = 0; i < formData.documents.length; i++) {
                const doc = formData.documents[i];
                fd.append(`documents[${i}][type_doc]`, doc.type_doc);
                fd.append(`documents[${i}][title_doc]`, doc.title_doc);
                if (doc.type_doc === 'image') {
                    fd.append(`documents[${i}][legend_doc]`, doc.legend_doc);
                    fd.append(`documents[${i}][number_doc]`, doc.number_doc);
                }
                fd.append(`documents[${i}][auth_doc]`, doc.auth_doc);
                const sources = {};
                doc.source_doc.forEach((src) => {
                    if (src.key && src.value) {
                        sources[src.key] = src.value;
                    }
                });
                fd.append(`documents[${i}][source_doc]`, JSON.stringify(sources));
                if (doc.path_doc && typeof doc.path_doc !== "string") {
                    fd.append(`documents[${i}][path_doc]`, doc.path_doc);
                }
            }
            await submitArticle(fd);
            setCurrentStep(4);
        } catch (err) {
            setSubmissionError(
                err.response?.data?.message ||
                err.message ||
                'Une erreur s\'est produite lors de la soumission. Veuillez réessayer.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const LanguageSelector = () => (
        <div className="flex items-center mb-3 gap-4">
            <div className="text-sm text-blueprimary font-medium">{t('language_selector_label')}</div>
            <div className="flex gap-4">
                {LANGS.map(l => (
                    <label className="flex items-center" key={l.code}>
                        <input
                            type="radio"
                            name="language"
                            value={l.code}
                            checked={selectedLanguage === l.code}
                            onChange={handleLanguageChange}
                            className="h-4 w-4 text-greenprimary focus:ring-greenlogo"
                        />
                        <span className="ml-2 text-sm text-blueprimary">{l.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );

    const getStepState = (step) => {
        if (currentStep === step) return "active";
        if (currentStep > step) return "completed";
        return "pending";
    };

    const stepsDesktop = [
        { id: 1, label: "Informations Personnelles" },
        { id: 2, label: "Détails Article" },
        { id: 3, label: "Documents" },
        { id: 4, label: "Attente de résultat" }
    ];

    const stepsMobile = [
        { id: 1, label: "Informations" },
        { id: 2, label: "Détails" },
        { id: 3, label: "Documents" },
        { id: 4, label: "Résultat" }
    ];

    const renderDesktopProgressBar = () => (
        <div className="w-full mb-6 hidden sm:block">
            <div className="flex items-center justify-between">
                {stepsDesktop.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <div className="relative flex flex-col items-center">
                            <div className={`w-7 h-7 flex items-center justify-center rounded-full ${getStepState(step.id) === "active"
                                ? "bg-greenprimary text-white"
                                : getStepState(step.id) === "completed"
                                    ? "bg-greenprimary text-white"
                                    : "bg-greensecondary text-blueprimary"
                                } z-10`}>
                                {getStepState(step.id) === "completed" ? (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                ) : (
                                    step.id
                                )}
                            </div>
                            <p className={`text-xs mt-1 ${getStepState(step.id) === "active"
                                ? "text-greenprimary font-medium"
                                : getStepState(step.id) === "completed"
                                    ? "text-greenprimary"
                                    : "text-blueprimary"
                                }`}>
                                {step.label}
                            </p>
                        </div>
                        {index < stepsDesktop.length - 1 && (
                            <div className={`flex-1 h-0.5 ${getStepState(stepsDesktop[index + 1].id) === "completed" ? "bg-greenprimary" : "bg-greensecondary"}`}></div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    const renderMobileProgressBar = () => (
        <div className="w-full mb-6 sm:hidden">
            <div className="flex items-center justify-between">
                {stepsMobile.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <div className="relative flex flex-col items-center">
                            <div className={`w-6 h-6 flex items-center justify-center rounded-full ${getStepState(step.id) === "active"
                                ? "bg-greenprimary text-white"
                                : getStepState(step.id) === "completed"
                                    ? "bg-greenprimary text-white"
                                    : "bg-greensecondary text-blueprimary"
                                } z-10 text-xs`}>
                                {getStepState(step.id) === "completed" ? (
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                ) : (
                                    step.id
                                )}
                            </div>
                            <p className={`text-[10px] mt-1 ${getStepState(step.id) === "active"
                                ? "text-greenprimary font-medium"
                                : getStepState(step.id) === "completed"
                                    ? "text-greenprimary"
                                    : "text-blueprimary"
                                }`}>
                                {step.label}
                            </p>
                        </div>
                        {index < stepsMobile.length - 1 && (
                            <div className={`flex-1 h-0.5 ${getStepState(stepsMobile[index + 1].id) === "completed"
                                ? "bg-greenprimary"
                                : "bg-greensecondary"
                                }`}></div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="form-step">

            <h2 className="text-lg font-bold mb-3 text-blueprimary">Étape 1: Informations de l'auteur principal</h2>
            <div className="mb-4">
                <h3 className="text-md font-semibold mb-2 text-blueprimary">Auteur principal</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block mb-1 text-sm text-blueprimary">Titre (Dr., Prof., etc.)</label>
                        <input type="text" name="title_aut" value={formData.author.title_aut} onChange={handleAuthorChange}
                            className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="block mb-1 text-sm text-blueprimary">Prénom <span className="text-danger">*</span></label>
                        <input type="text" name="first_name" value={formData.author.first_name} onChange={handleAuthorChange}
                            className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm"
                            required />
                        {fieldErrors.author_first_name && <span className="text-danger text-xs">{fieldErrors.author_first_name}</span>}
                    </div>
                    <div>
                        <label className="block mb-1 text-sm text-blueprimary">Nom <span className="text-danger">*</span></label>
                        <input type="text" name="last_name" value={formData.author.last_name} onChange={handleAuthorChange}
                            className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm"
                            required />
                        {fieldErrors.author_last_name && <span className="text-danger text-xs">{fieldErrors.author_last_name}</span>}
                    </div>
                    <div>
                        <label className="block mb-1 text-sm text-blueprimary">Email <span className="text-danger">*</span></label>
                        <input type="email" name="email_user" value={formData.author.email_user} onChange={handleAuthorChange}
                            className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm"
                            required />
                        {fieldErrors.author_email_user && <span className="text-danger text-xs">{fieldErrors.author_email_user}</span>}
                    </div>
                    <div
                        className="col-span-1 sm:col-span-2 relative"
                    >
                        <label className="block mb-1 text-sm text-blueprimary">ORCID</label>
                        <input type="text" name='orch_id' value={formData.author.orch_id} onChange={handleAuthorChange}
                            className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm"
                        />
                    </div>

                    <div
                        className="col-span-1 sm:col-span-2 relative"
                        ref={(el) => affiliationInputRefs.current['main'] = el}
                    >
                        <label className="block mb-1 text-sm text-blueprimary">
                            Affiliations <span className="text-danger">*</span>
                            <span className='text-xs text-greenlogo italic'> (Saisir puis appuyer sur entrée pour ajouter. Plusieurs affiliations possibles.)</span>
                        </label>
                        <div className="flex flex-wrap gap-2 border bg-inputbackground border-greenlogo p-2 rounded-md min-h-[42px]">
                            {formData.author.affiliations.map((a, i) => (
                                <span key={a + i} className="flex items-center bg-greenlogo px-2 py-1 rounded text-blueprimary text-xs font-medium">
                                    {a}
                                    <button type="button" className="ml-1 text-danger hover:text-danger font-bold"
                                        aria-label="Supprimer l'affiliation"
                                        onClick={() => removeAffiliation(-1, a)}>×</button>
                                </span>
                            ))}
                            <input
                                type="text"
                                name="affiliations"
                                value={activeAuthorIndex === -1 ? currentAffiliationInput : ''}
                                onChange={(e) => handleAffiliationInputChange(e, -1)}
                                onKeyDown={(e) => handleAffiliationInputKeyDown(e, -1)}
                                onFocus={() => {
                                    setActiveAuthorIndex(-1);
                                    setCurrentAffiliationInput('');
                                    setShowAffiliationSuggestions(true);
                                }}
                                className="flex-1 min-w-[90px] bg-[#0000] border-0 outline-none text-sm appearance-none focus:ring-0"
                                placeholder="Ajouter une affiliation..."
                                required
                            />
                        </div>
                        {fieldErrors.author_affiliations && <span className="text-danger text-xs">{fieldErrors.author_affiliations}</span>}
                        {showAffiliationSuggestions && activeAuthorIndex === -1 && filteredAffiliations.length > 0 && (
                            <ul className="absolute z-10 w-full bg-white shadow-lg border border-greenlogo rounded-md mt-1 max-h-40 overflow-y-auto">
                                {filteredAffiliations.map((aff, idx) => (
                                    <li
                                        key={idx}
                                        onClick={() => handleAffiliationSelect(aff)}
                                        className="px-3 py-2 text-sm hover:bg-greenlogo hover:bg-opacity-10 cursor-pointer"
                                    >{aff}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            <div className="mb-4">
                <div className="flex flex-wrap justify-between items-center mb-2">
                    <h3 className="text-md font-semibold text-blueprimary">Co-auteurs</h3>
                    <button type="button" onClick={addCoAuthor}
                        className="bg-greenprimary text-white px-2 py-1 text-sm mt-1 sm:mt-0">+ Ajouter</button>
                </div>
                {formData.co_authors.map((coAuthor, index) => (
                    <div key={index} className="p-3 border border-greenlogo rounded-md mb-3 bg-greensecondary bg-opacity-10">
                        <div className="flex flex-wrap justify-between mb-2">
                            <h4 className="text-sm font-medium text-blueprimary">Co-auteur #{index + 1}</h4>
                            {formData.co_authors.length > 1 && (
                                <button type="button" onClick={() => removeCoAuthor(index)}
                                    className="text-danger text-sm hover:text-opacity-80 mt-1 sm:mt-0">Supprimer</button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block mb-1 text-xs text-blueprimary">Titre</label>
                                <input type="text" name="title_aut" value={coAuthor.title_aut}
                                    onChange={e => handleCoAuthorChange(index, e)}
                                    className="w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm" />
                            </div>
                            <div>
                                <label className="block mb-1 text-xs text-blueprimary">Prénom <span className="text-danger">*</span></label>
                                <input type="text" name="first_name" value={coAuthor.first_name}
                                    onChange={e => handleCoAuthorChange(index, e)}
                                    className="w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm" />
                                {fieldErrors[`coauthor_${index}_first_name`] && <span className="text-danger text-xs">{fieldErrors[`coauthor_${index}_first_name`]}</span>}
                            </div>
                            <div>
                                <label className="block mb-1 text-xs text-blueprimary">Nom <span className="text-danger">*</span></label>
                                <input type="text" name="last_name" value={coAuthor.last_name}
                                    onChange={e => handleCoAuthorChange(index, e)}
                                    className="w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm" />
                                {fieldErrors[`coauthor_${index}_last_name`] && <span className="text-danger text-xs">{fieldErrors[`coauthor_${index}_last_name`]}</span>}
                            </div>
                            <div>
                                <label className="block mb-1 text-xs text-blueprimary">Email <span className="text-danger">*</span></label>
                                <input type="email" name="email_user" value={coAuthor.email_user}
                                    onChange={e => handleCoAuthorChange(index, e)}
                                    className="w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm" />
                                {fieldErrors[`coauthor_${index}_email_user`] && <span className="text-danger text-xs">{fieldErrors[`coauthor_${index}_email_user`]}</span>}
                            </div>
                            <div
                                className="col-span-1 sm:col-span-2 relative"
                            >
                                <label className='block mb-1 text-xs text-blueprimary'>ORCID</label>
                                <input type="text" name='orch_id' value={coAuthor.orch_id}
                                    onChange={e => handleCoAuthorChange(index, e)}
                                    className='w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm' />
                            </div>
                            <div
                                className="col-span-1 sm:col-span-2 relative"
                                ref={(el) => affiliationInputRefs.current[`coauthor_${index}`] = el}
                            >
                                <label className="block mb-1 text-xs text-blueprimary">
                                    Affiliations <span className="text-danger">*</span>
                                    <span className='text-xs text-greenlogo italic'> (Saisir puis appuyer sur entrée. Plusieurs affiliations possibles.)</span>
                                </label>
                                <div className="flex flex-wrap gap-2 border bg-inputbackground border-greenlogo p-2 rounded-md min-h-[42px]">
                                    {coAuthor.affiliations.map((a, i) => (
                                        <span key={a + i} className="flex items-center bg-greenlogo px-2 py-1 rounded text-blueprimary text-xs font-medium">
                                            {a}
                                            <button type="button" className="ml-1 text-danger hover:text-danger font-bold"
                                                aria-label="Supprimer l'affiliation"
                                                onClick={() => removeAffiliation(index, a)}>×</button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        name="affiliations"
                                        value={activeAuthorIndex === index ? currentAffiliationInput : ''}
                                        onChange={(e) => handleAffiliationInputChange(e, index)}
                                        onKeyDown={(e) => handleAffiliationInputKeyDown(e, index)}
                                        onFocus={() => {
                                            setActiveAuthorIndex(index);
                                            setCurrentAffiliationInput('');
                                            setShowAffiliationSuggestions(true);
                                        }}
                                        className="flex-1 min-w-[90px] bg-[#0000] border-0 outline-none text-sm appearance-none focus:ring-0"
                                        placeholder="Ajouter une affiliation..."
                                    />
                                </div>
                                {fieldErrors[`coauthor_${index}_affiliations`] && <span className="text-danger text-xs">{fieldErrors[`coauthor_${index}_affiliations`]}</span>}
                                {showAffiliationSuggestions && activeAuthorIndex === index && filteredAffiliations.length > 0 && (
                                    <ul className="absolute z-10 w-full bg-white shadow-lg border border-greenlogo rounded-md mt-1 max-h-40 overflow-y-auto">
                                        {filteredAffiliations.map((aff, idx) => (
                                            <li
                                                key={idx}
                                                onClick={() => handleAffiliationSelect(aff)}
                                                className="px-3 py-2 text-xs hover:bg-greenlogo hover:bg-opacity-10 cursor-pointer"
                                            >{aff}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-end">
                <button type="button" onClick={nextStep}
                    className="bg-greenprimary text-white px-4 py-2 text-sm font-medium w-full sm:w-auto">Suivant</button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="form-step">
            <h2 className="text-lg font-bold mb-3 text-blueprimary">{t('step2_title')}</h2>
            <div className="grid gap-3">
                <div className="border-b border-greenlogo pb-3 mb-1">
                    <LanguageSelector />
                </div>
                <div>
                    <label className="block mb-1 text-sm text-blueprimary">
                        {t('title')} <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        name="title_art"
                        value={formData.title_art[selectedLanguage]}
                        onChange={handleArticleChange}
                        placeholder={t('title_placeholder')}
                        className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm"
                        required
                    />
                    {fieldErrors.title_art && <span className="text-danger text-xs">{fieldErrors.title_art}</span>}
                </div>
                <div>
                    <label className="block mb-1 text-sm text-blueprimary">
                        {t('resume')} <span className="text-danger">*</span>
                        <span className="ml-2 text-xs text-greenlogo italic">({t('summary_word_limit')})</span>
                    </label>
                    <textarea
                        name="resume_art"
                        value={formData.resume_art[selectedLanguage]}
                        onChange={handleArticleChange}
                        placeholder={t('resume_placeholder')}
                        className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md h-24 text-sm"
                        required
                        maxLength={2200}
                    />
                    <div className="flex justify-between text-xs mt-1">
                        <span>
                            {t('abstract_word_count')(formData.resume_art[selectedLanguage]?.trim().split(/\s+/).length || 0)}
                        </span>
                        {fieldErrors.resume_art && <span className="text-danger">{fieldErrors.resume_art}</span>}
                    </div>
                </div>
                <div>
                    <label className="block mb-1 text-sm text-blueprimary">
                        {t('keywords')} <span className="text-danger">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2 border bg-inputbackground border-greenlogo p-2 rounded-md min-h-[42px]">
                        {formData.key_words_art[selectedLanguage].map((k, i) => (
                            <span key={k + i} className="flex items-center bg-greenlogo px-2 py-1 rounded text-blueprimary text-xs font-medium">
                                {k}
                                <button type="button" className="ml-1 text-danger hover:text-danger font-bold"
                                    aria-label="Supprimer le mot-clé"
                                    onClick={() => removeKeyword(k)}>×</button>
                            </span>
                        ))}
                        <input
                            ref={keywordInputRef}
                            type="text"
                            className="flex-1 min-w-[90px] bg-[#0000] border-0 outline-none text-sm appearance-none focus:ring-0"
                            placeholder={formData.key_words_art[selectedLanguage].length ? '' : t('keywords_placeholder')}
                            value={currentKeyword}
                            onChange={handleKeywordInput}
                            onKeyDown={handleKeywordKeyDown}
                            onBlur={() => addKeyword(currentKeyword)}
                        />
                    </div>
                    {fieldErrors.key_words_art && <span className="text-danger text-xs">{fieldErrors.key_words_art}</span>}
                </div>
                <div>
                    <label className="block mb-1 text-sm text-blueprimary">{t('study_area_label')} <span className="text-xs text-blueprimary">{t('study_area_optional')}</span></label>
                    <input type="text" name="study_area_art" value={formData.study_area_art}
                        onChange={handleArticleChange}
                        placeholder={t('study_area_placeholder')}
                        className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-sm" />
                </div>
                <div>
                    <label className="block mb-1 text-sm text-blueprimary">{t('rubrique_label')} <span className="text-danger">{t('rubrique_required')}</span></label>
                    {loading ? (
                        <div className="w-full bg-inputbackground border border-greenlogo p-2 rounded-md text-blueprimary opacity-75 text-sm">
                            Chargement...
                        </div>
                    ) : error ? (
                        <div className="w-full bg-danger bg-opacity-10 border border-danger p-2 rounded-md text-danger text-sm">
                            {error}
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2 border bg-inputbackground border-greenlogo p-2 rounded-md min-h-[42px]">
                                {formData.id_rub.map((id) => {
                                    const rubrique = rubriques.find(r => r.id_rub === id);
                                    return (
                                        <span
                                            key={id}
                                            className="flex items-center bg-greenlogo px-2 py-1 rounded text-blueprimary text-xs font-medium"
                                        >
                                            {rubrique?.name_rub}
                                            <button
                                                type="button"
                                                className="ml-1 text-danger hover:text-danger font-bold"
                                                aria-label="Supprimer la rubrique"
                                                onClick={() => {
                                                    const updatedRubriques = formData.id_rub.filter(rubId => rubId !== id);
                                                    setFormData(f => ({ ...f, id_rub: updatedRubriques }));
                                                }}
                                            >×</button>
                                        </span>
                                    );
                                })}
                                <div className="relative flex-1 min-w-[120px]">
                                    <select
                                        className="w-full bg-[#0000] border-0 outline-none text-sm appearance-none focus:ring-0"
                                        value=""
                                        onChange={(e) => {
                                            const selectedId = Number(e.target.value);
                                            if (selectedId && !formData.id_rub.includes(selectedId)) {
                                                setFormData(f => ({
                                                    ...f,
                                                    id_rub: [...f.id_rub, selectedId]
                                                }));
                                            }
                                            e.target.value = "";
                                        }}
                                    >
                                        <option value="">{t("add_rubrique")}</option>
                                        {rubriques
                                            .filter(r => !formData.id_rub.includes(r.id_rub))
                                            .map(rubrique => (
                                                <option key={rubrique.id_rub} value={rubrique.id_rub} className="px-auto text-sm text-blueprimary">
                                                    {rubrique.name_rub}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>
                        </>
                    )}
                    {fieldErrors.id_rub && <span className="text-danger text-xs block">{fieldErrors.id_rub}</span>}
                </div>
                <div>
                    <label className="block mb-1 text-sm text-blueprimary">
                        {t('file_main_label')} <span className="text-danger">*</span>
                    </label>
                    <div className="mb-1 text-xs text-blueprimary opacity-70">{t('file_main_format')}</div>
                    <div className={`relative ${fileMainValid === true ? "border-2 border-blueprimary" : fileMainValid === false ? "border-2 border-danger" : "border-2 border-greenlogo"} border-dashed rounded-md bg-inputbackground hover:bg-greenlogo hover:bg-opacity-5 transition-colors`}>
                        {formData.file_art ? (
                            <div className="flex items-center p-3 bg-greensecondary bg-opacity-10 border border-greenlogo rounded-md">
                                <div className="mr-3 text-greenprimary">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div className="flex-grow">
                                    <div className="text-sm font-medium text-blueprimary truncate" title={formData.file_art.name}>
                                        {formData.file_art.name}
                                    </div>
                                    <div className="text-xs text-greenprimary">
                                        {(formData.file_art.size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                    {fileMainValid === true && (
                                        <span className="text-xs text-blueprimary font-semibold">{t("file_main_valid")}</span>
                                    )}
                                    {fileMainValid === false && (
                                        <span className="text-xs text-danger font-semibold">{t("file_main_error")}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData(f => ({ ...f, file_art: null }));
                                        setFileMainValid(null);
                                    }}
                                    className="ml-2 text-bluesecondary hover:text-danger transition-colors"
                                    title="Supprimer le fichier"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <div>
                                <label className="flex flex-col items-center justify-center py-6 cursor-pointer">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-greenlogo" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <span className="mt-2 text-sm text-blueprimary font-medium">{t("file_main_placeholder")}</span>
                                    <span className="mt-1 text-xs text-blueprimary">ou glissez-déposez votre fichier ici</span>
                                    <input
                                        type="file"
                                        name="file_art"
                                        accept=".doc,.docx"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        required
                                    />
                                </label>
                            </div>
                        )}
                    </div>
                    {fieldErrors.file_art && <span className="text-danger text-xs block mt-2">{fieldErrors.file_art}</span>}
                </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-2 mt-4">
                <button type="button" onClick={prevStep}
                    className="bg-bluesecondary text-white px-4 py-2 text-sm font-medium order-2 sm:order-1">Précédent</button>
                <button type="button" onClick={nextStep}
                    className="bg-greenprimary text-white px-4 py-2 text-sm font-medium order-1 sm:order-2">Suivant</button>
            </div>
        </div>
    );

    const renderStep3 = () => {
        // Figure/Image obligatoire : au moins une image doit être ajoutée
        const imageDocs = formData.documents.filter((doc) => doc.type_doc === 'image');
        const showGlobalError =
            imageDocs.length === 0 ||
            imageDocs.some((doc) => !doc.title_doc.trim() || !doc.source_doc.every(src => src.key.trim() && src.value.trim()) || !doc.path_doc);

        // Condition pour bloquer le bouton "Soumettre"
        const canSubmitDocuments =
            imageDocs.length > 0 &&
            imageDocs.every((doc) =>
                doc.title_doc.trim() &&
                doc.source_doc.every(src => src.key.trim() && src.value.trim()) &&
                doc.path_doc
            );

        return (
            <div className="form-step">
                <h2 className="text-lg font-bold mb-3 text-blueprimary">
                    Étape 3: Documents et annexes <span className="text-danger">*</span>
                </h2>
                <div className="mb-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            type="button"
                            onClick={() => handleDocumentTypeAdd('image')}
                            className="bg-greenprimary text-white px-3 py-1.5 rounded text-xs flex items-center gap-1"
                        >
                            + Ajouter une Figure/Image <span className="text-danger font-bold">*</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleDocumentTypeAdd('pdf')}
                            className="bg-greenprimary text-white px-3 py-1.5 rounded text-xs"
                        >+ Ajouter un PDF</button>
                    </div>
                    {/* Message d'erreur général si aucune figure/image ou champs obligatoires manquants */}
                    {showGlobalError && (
                        <div className="mb-3 p-2 bg-danger bg-opacity-10 border border-danger rounded text-danger text-sm">
                            Il est obligatoire d'ajouter au moins une Figure/Image et de remplir les champs suivants :
                            <ul className="list-disc ml-5 mt-1">
                                <li>Titre du document <span className="text-danger">*</span></li>
                                <li>Sources <span className="text-danger">*</span></li>
                                <li>Fichier <span className="text-danger">*</span></li>
                            </ul>
                        </div>
                    )}
                    {formData.documents.length === 0 && (
                        <div className="text-xs text-blueprimary mb-2">Aucune annexe ajoutée</div>
                    )}
                    {formData.documents.map((doc, index) => {
                        const titleRequired = doc.type_doc === 'image';
                        const sourcesRequired = doc.type_doc === 'image';
                        const fileRequired = doc.type_doc === 'image';

                        return (
                            <div key={index} className="p-3 border border-greenlogo rounded-md mb-3 bg-greensecondary bg-opacity-10">
                                <div className="flex flex-wrap justify-between mb-2">
                                    <h4 className="text-sm font-medium text-blueprimary">
                                        {doc.type_doc === 'image' ? "Figure/Image" : "PDF"} #{index + 1}
                                    </h4>
                                    <button type="button" onClick={() => removeDocument(index)}
                                        className="text-danger text-sm hover:text-opacity-80 mt-1 sm:mt-0">Supprimer</button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block mb-1 text-xs text-blueprimary">
                                            Titre du document {titleRequired && <span className="text-danger">*</span>}
                                        </label>
                                        <input
                                            type="text"
                                            name="title_doc"
                                            value={doc.title_doc}
                                            onChange={e => handleDocumentChange(index, e)}
                                            className={`w-full bg-inputbackground border rounded-md text-sm p-1.5 ${titleRequired && !doc.title_doc.trim()
                                                ? 'border-danger'
                                                : 'border-greenlogo'
                                                }`}
                                            required={titleRequired}
                                        />
                                        {(fieldErrors[`doc_${index}_title_doc`] || (titleRequired && !doc.title_doc.trim())) && (
                                            <span className="text-danger text-xs">
                                                {fieldErrors[`doc_${index}_title_doc`] || "Le titre est obligatoire pour les Figures/Images."}
                                            </span>
                                        )}
                                    </div>
                                    {doc.type_doc === 'image' && (
                                        <>
                                            <div>
                                                <label className="block mb-1 text-xs text-blueprimary">Numéro de la figure</label>
                                                <input type="text" name="number_doc" value={doc.number_doc}
                                                    onChange={e => handleDocumentChange(index, e)}
                                                    className="w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm" />
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs text-blueprimary">Légende</label>
                                                <input type="text" name="legend_doc" value={doc.legend_doc}
                                                    onChange={e => handleDocumentChange(index, e)}
                                                    className="w-full bg-inputbackground border border-greenlogo p-1.5 rounded-md text-sm" />
                                            </div>
                                        </>
                                    )}
                                    <div className="col-span-1 sm:col-span-2">
                                        <label className="block mb-1 text-xs text-blueprimary">
                                            Sources {sourcesRequired && <span className="text-danger">*</span>}
                                        </label>
                                        {doc.source_doc.map((src, srcIdx) => (
                                            <div key={srcIdx} className="flex items-center mb-1">
                                                <input
                                                    type="text"
                                                    placeholder="Nom de la source"
                                                    value={src.key}
                                                    onChange={e => handleDocumentSourceChange(index, srcIdx, 'key', e.target.value)}
                                                    className={`w-1/3 bg-inputbackground border rounded-md text-xs mr-2 p-1.5 ${sourcesRequired && !src.key.trim()
                                                        ? 'border-danger'
                                                        : 'border-greenlogo'
                                                        }`}
                                                    required={sourcesRequired}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Valeur de la source"
                                                    value={src.value}
                                                    onChange={e => handleDocumentSourceChange(index, srcIdx, 'value', e.target.value)}
                                                    className={`w-2/3 bg-inputbackground border rounded-md text-xs mr-2 p-1.5 ${sourcesRequired && !src.value.trim()
                                                        ? 'border-danger'
                                                        : 'border-greenlogo'
                                                        }`}
                                                    required={sourcesRequired}
                                                />
                                                {doc.source_doc.length > 1 && (
                                                    <button type="button" className="text-danger text-xs" onClick={() => removeDocumentSource(index, srcIdx)}>X</button>
                                                )}
                                            </div>
                                        ))}
                                        {(fieldErrors[`doc_${index}_source_doc`] || (sourcesRequired && !doc.source_doc.every(src => src.key.trim() && src.value.trim()))) && (
                                            <span className="text-danger text-xs">
                                                {fieldErrors[`doc_${index}_source_doc`] || "Les sources sont obligatoires pour les Figures/Images."}
                                            </span>
                                        )}
                                        <button type="button" className="text-greenprimary text-xs mt-1" onClick={() => addDocumentSource(index)}>+ Ajouter une source</button>
                                    </div>
                                    <div className="col-span-1 sm:col-span-2">
                                        {doc.type_doc === 'image' ? (
                                            <label className="block mb-1 text-xs text-blueprimary">Fichier (jpg/png/jpeg/gif/bmp/webp) <span className="text-danger">*</span></label>
                                        ) : (
                                            <label className="block mb-1 text-xs text-blueprimary">Fichier PDF (optionnel)</label>
                                        )}
                                        <div className="relative">
                                            {doc.path_doc ? (
                                                <div className="flex items-center p-3 bg-greensecondary bg-opacity-10 border border-greenlogo rounded-md">
                                                    <div className="mr-3 text-greenprimary">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </div>
                                                    <div className="flex-grow">
                                                        <div className="text-sm font-medium text-blueprimary truncate" title={typeof doc.path_doc === 'object' ? doc.path_doc.name : doc.path_doc}>
                                                            {typeof doc.path_doc === 'object' ? doc.path_doc.name : doc.path_doc}
                                                        </div>
                                                        {typeof doc.path_doc === 'object' && (
                                                            <div className="text-xs text-greenprimary">
                                                                {(doc.path_doc.size / 1024 / 1024).toFixed(2)} MB
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updatedDocs = [...formData.documents];
                                                            updatedDocs[index].path_doc = null;
                                                            setFormData({ ...formData, documents: updatedDocs });
                                                        }}
                                                        className="ml-2 text-bluesecondary hover:text-danger transition-colors"
                                                        title="Supprimer le fichier"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="border-2 border-dashed border-greenlogo rounded-md bg-inputbackground hover:bg-greenlogo hover:bg-opacity-5 transition-colors">
                                                    <label className="flex flex-col items-center justify-center py-6 cursor-pointer">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-greenlogo" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-blueprimary font-medium">Cliquez pour sélectionner un fichier</span>
                                                        <span className="mt-1 text-xs text-blueprimary">ou glissez-déposez votre fichier ici</span>
                                                        <input
                                                            type="file"
                                                            name="path_doc"
                                                            accept={doc.type_doc === 'image' ? ".jpg,.jpeg,.png,.gif,.bmp,.webp" : ".pdf"}
                                                            onChange={e => handleDocumentChange(index, e)}
                                                            className="hidden"
                                                            required={fileRequired}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                        {(fieldErrors[`doc_${index}_path_doc`] || (fileRequired && !doc.path_doc)) && (
                                            <span className="text-danger text-xs block mt-2">
                                                {fieldErrors[`doc_${index}_path_doc`] || "Le fichier est obligatoire pour les Figures/Images."}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <p className="text-xs text-blueprimary mt-2">
                        Les annexes peuvent être une figure/image (avec numéro, légende, source) ou un PDF (avec source). Les PDF sont optionnels.
                    </p>
                </div>
                {submissionError && (
                    <div className="mt-3 p-2 bg-danger bg-opacity-10 border border-danger rounded-md text-danger text-sm">
                        {submissionError}
                    </div>
                )}
                <div className="flex flex-col sm:flex-row justify-between gap-2 mt-4">
                    <button type="button" onClick={prevStep}
                        className="bg-bluesecondary text-white px-4 py-2 text-sm font-medium order-2 sm:order-1">Précédent</button>
                    <button type="button" onClick={handleSubmit}
                        className="bg-greenprimary text-white px-4 py-2 text-sm font-medium order-1 sm:order-2"
                        disabled={!canSubmitDocuments || submitting}>
                        {submitting ? 'En cours...' : 'Soumettre'}
                    </button>
                </div>
            </div>
        );
    };

    const renderStep4 = () => (
        <div className="form-step text-center py-4">
            <div className="mb-3 text-greenprimary">
                <svg className="mx-auto h-10 w-10" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
            </div>
            <h2 className="text-xl font-bold mb-2 text-blueprimary">Soumission réussie !</h2>
            <p className="text-bluesecondary mb-3 text-sm">
                Votre article a été soumis avec succès. Nous l'examinerons et vous contacterons prochainement.
            </p>
            <Link to={routes.home} className="bg-greenprimary text-white px-4 py-2 text-sm font-medium inline-block">
                Retour à l'accueil
            </Link>
        </div>
    );

    const renderForm = () => {
        switch (currentStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            case 4: return renderStep4();
            default: return renderStep1();
        }
    };

    return (
        <div className="w-full max-w-xs sm:max-w-md md:max-w-lg lg:max-w-2xl mx-auto px-3 sm:px-4 mt-10">
            {currentStep <= 4 && (
                <>
                    {renderDesktopProgressBar()}
                    {renderMobileProgressBar()}
                </>
            )}
            <form className="bg-white p-3 sm:p-4 rounded-md border border-greensecondary border-opacity-20">
                {renderForm()}
            </form>
        </div>
    );
};

export default PropositionArticle;